import dayjs from 'dayjs'
import sortBy from 'lodash/sortBy'

import { calculateProratedQuota } from '@vacationtracker/shared/functions/calculation-shared'
import { QuotaUnits } from '@vacationtracker/shared/types/leave-policy'
import { AwardSeniorityEntitlementDaysOn, SeniorityEntitlementCalculateFrom } from '@vacationtracker/shared/types/automations'

export type SeniorityPreviewTierStatus = 'credited' | 'skipped' | 'upcoming'

export interface ISeniorityPreviewPeriod {
  years: number
  amount: number
  units: QuotaUnits
}

export interface ISeniorityPreviewParams {
  startDate: string
  now: Date
  periods: ISeniorityPreviewPeriod[]
  calculateFrom?: SeniorityEntitlementCalculateFrom
  awardSeniorityEntitlementDaysOn?: AwardSeniorityEntitlementDaysOn
  prorate?: boolean
  resetQuotas?: string
  yearStartMonth?: number
  yearStartDay?: number
}

export interface ISeniorityPreviewTier {
  years: number
  amount: number
  status: SeniorityPreviewTierStatus
  // Present only when the award is split across fiscal years (prorate + work anniversary + fiscal-year location).
  split?: {
    anniversary: number
    fiscal: number
  }
}

export interface ISeniorityPreviewResult {
  seniorityYears: number
  units: QuotaUnits
  tiers: ISeniorityPreviewTier[]
  pastAmount: number
  futureAmount: number
  totalAmount: number
}

/**
 * Picks the location whose fiscal-year settings drive the preview for a set of scoped locations:
 * a USER_YEAR location takes precedence (it forces the work-anniversary/no-split behavior),
 * otherwise the first scoped location. Shared by the create form and the list drawer.
 */
export function pickScopedLocation<T extends { id: string; resetQuotas?: string }>(scopedIds: string[], locations: T[]): T | undefined {
  const scoped = scopedIds.map((id) => locations.find((location) => location.id === id)).filter(Boolean) as T[]
  return scoped.find((location) => location.resetQuotas === 'USER_YEAR') ?? scoped[0]
}

/**
 * Read-only simulation of the Seniority Entitlement automation for the preview panel.
 * Mirrors the backend award logic (seniority-entitlement/lib/main.ts anniversary award and
 * common/seniority-entitlement-repository.ts addDaysInThePast) without changing it:
 *  - a tier lands on the work anniversary, or on the fiscal-year start when awarding
 *    at BEGINNING_OF_THE_FISCAL_YEAR for FISCAL_YEAR locations;
 *  - tiers already reached count only when calculateFrom === USER_EMPLOYMENT_DATE
 *    (TODAY ignores tiers already passed);
 *  - tiers not yet reached are always awarded on their upcoming date.
 * Proration only splits the same amount across two dates, so totals are unaffected.
 */
export function calculateSeniorityPreview(params: ISeniorityPreviewParams): ISeniorityPreviewResult {
  const { startDate, now, calculateFrom, awardSeniorityEntitlementDaysOn, prorate, resetQuotas, yearStartMonth, yearStartDay } = params

  const periods = sortBy(params.periods, ['years'])
  const units: QuotaUnits = periods[0]?.units ?? 'days'
  // The backend reads startDate's date part and awards at 00:00 UTC, so compare UTC calendar dates
  // (both parsed at the same local midnight) instead of instants in the browser's timezone
  const nowDay = dayjs(now.toISOString().split('T')[0])
  const employmentStart = dayjs(startDate.split('T')[0])
  // Completed months, so the next year shows only from the anniversary itself
  const seniorityYears = Math.max(0, Math.floor((nowDay.diff(employmentStart, 'month') * 10) / 12) / 10)

  const hasFiscalYear = resetQuotas === 'FISCAL_YEAR' && typeof yearStartMonth === 'number' && typeof yearStartDay === 'number'
  const awardOnFiscalStart = hasFiscalYear && awardSeniorityEntitlementDaysOn === 'BEGINNING_OF_THE_FISCAL_YEAR'
  const splitAcrossFiscalYears = Boolean(prorate) && hasFiscalYear && awardSeniorityEntitlementDaysOn === 'USER_WORK_ANNIVERSARY'

  const tiers: ISeniorityPreviewTier[] = periods.map((period) => {
    const anniversary = employmentStart.add(period.years, 'year')
    const landing = awardOnFiscalStart ? anniversary.set('month', (yearStartMonth as number) - 1).set('date', yearStartDay as number) : anniversary

    const reached = nowDay.isSame(landing) || nowDay.isAfter(landing)

    let status: SeniorityPreviewTierStatus
    if (!reached) {
      status = 'upcoming'
    } else {
      status = calculateFrom === 'USER_EMPLOYMENT_DATE' ? 'credited' : 'skipped'
    }

    const tier: ISeniorityPreviewTier = { years: period.years, amount: period.amount, status }

    if (splitAcrossFiscalYears) {
      const split = splitTierAcrossFiscalYear({
        anniversary,
        amount: period.amount,
        units: period.units,
        yearStartMonth: yearStartMonth as number,
        yearStartDay: yearStartDay as number,
      })
      if (split) {
        tier.split = split
      }
    }

    return tier
  })

  const pastAmount = tiers.filter((tier) => tier.status === 'credited').reduce((acc, tier) => acc + tier.amount, 0)
  const futureAmount = tiers.filter((tier) => tier.status === 'upcoming').reduce((acc, tier) => acc + tier.amount, 0)

  return {
    seniorityYears,
    units,
    tiers,
    pastAmount,
    futureAmount,
    totalAmount: pastAmount + futureAmount,
  }
}

interface ISplitTierParams {
  anniversary: dayjs.Dayjs
  amount: number
  units: QuotaUnits
  yearStartMonth: number
  yearStartDay: number
}

/**
 * Mirrors the prorate split in seniority-entitlement-repository.ts addDaysInThePast:
 * the anniversary gets the portion of the fiscal year still remaining, the rest posts at the
 * next fiscal-year start. Returns null when the anniversary coincides with the fiscal-year start
 * (nothing to split).
 */
function splitTierAcrossFiscalYear(params: ISplitTierParams): { anniversary: number; fiscal: number } | null {
  const { anniversary, amount, units, yearStartMonth, yearStartDay } = params
  const periodYear = anniversary.year()

  let fiscalYearStart = dayjs(`${periodYear}-${yearStartMonth}-${yearStartDay}`)
  if (fiscalYearStart.isAfter(anniversary)) {
    fiscalYearStart = fiscalYearStart.subtract(1, 'year')
  }

  if (fiscalYearStart.format('YYYY-MM-DD') === anniversary.format('YYYY-MM-DD')) {
    return null
  }

  const prorated = calculateProratedQuota({
    quota: { unit: units, value: amount },
    yearStart: fiscalYearStart.toDate(),
    userStartDate: anniversary.toDate(),
  })
  const anniversaryAmount = Math.round(prorated.value * 10) / 10
  const fiscalAmount = Math.round((amount - anniversaryAmount) * 10) / 10
  return { anniversary: anniversaryAmount, fiscal: fiscalAmount }
}
